import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { BoxFull, Button, TransactionalSubheader } from "iuvity-ui-components";
import { Header } from "../../components";
import {
  NewCustomDialog,
  NewDialogProps,
} from "../../components/dialog/NewCustomDialog";
import { FunctionalitiesFormSchema } from "../../validations/schemas";
import { FUNCTIONALITIES_STATUS_OPTIONS, REVALIDATE_FORM_PROPS, calendarTranslations, functionalitiesFieldsSelecter } from "../../constants";
import { AppContext } from "../../context/index";
import { CustomFormComponent } from "../../components/shared/CustomFormComponent";
import { useFunctionalities } from "../../hooks/useFunctionalities";
import { useCategories } from "../../hooks/useCategories";
import { formatStringToDate2 } from "../../helpers/formatDate";
import { useConsoleTranslation } from "../../hooks/useConsoleTranslations";
import { createLabelButtonMaker } from "../../helpers/createLabelButtonMaker";

interface NewFunctionalitiesProps {
  update?: boolean;
  clone?: boolean;
  detail?: boolean;
}

const functionalityDefaultValues = {
  key: "",
  code: "",
  expeditionDate: null,
  expirationDate: null,
  categoryId: "",
  keyLabel: "",
  keyLabelDescription: "",
  parentId: null,
  isAdministrative: null,
};

export const NewFunctionalityPage: React.FC<NewFunctionalitiesProps> = ({ update = false, clone = false, detail = false }) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedFunctionality },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: (update || detail) ?
      {
        id: selectedFunctionality?.id,
        key: selectedFunctionality?.key,
        code: selectedFunctionality?.code,
        expeditionDate: detail
          ? formatStringToDate2(selectedFunctionality?.expeditionDate || '', true, t)
          : formatStringToDate2(selectedFunctionality?.expeditionDate || '', false),
        expirationDate: detail
          ? formatStringToDate2(selectedFunctionality?.expirationDate || '', true, t)
          : formatStringToDate2(selectedFunctionality?.expirationDate, false),
        categoryId: selectedFunctionality?.categoryId,
        keyLabel: selectedFunctionality?.keyLabel?.includes('Menu.') ? selectedFunctionality?.keyLabel?.split('.')[1] : selectedFunctionality?.keyLabel,
        keyLabelDescription: selectedFunctionality?.keyLabelDescription,
        parentId: selectedFunctionality?.parentId,
        statusId: selectedFunctionality?.statusId,
        updatedby: user?.name,
        isAdministrative: selectedFunctionality?.isAdministrative ? 'isAdministrative' : 'isApplication'
      } : clone ?
        {
          nametoclone: selectedFunctionality?.key,
          codetoclone: selectedFunctionality?.code,
          key: '',
          code: '',
          expeditionDate: !!selectedFunctionality?.expeditionDate ? formatStringToDate2(selectedFunctionality?.expeditionDate, false) : null,
          expirationDate: !!selectedFunctionality?.expirationDate ? formatStringToDate2(selectedFunctionality?.expirationDate, false) : null,
          categoryId: selectedFunctionality?.categoryId,
          keyLabel: selectedFunctionality?.keyLabel?.includes('Menu.') ? selectedFunctionality?.keyLabel?.split('.')[1] : selectedFunctionality?.keyLabel,
          keyLabelDescription: selectedFunctionality?.keyLabelDescription,
          parentId: selectedFunctionality?.parentId,
          createdby: user?.name,
          isAdministrative: selectedFunctionality?.isAdministrative ? 'isAdministrative' : 'isApplication'
        } :
        {
          ...functionalityDefaultValues,
          createdby: user?.name
        }
  });

  const {
    getValues,
    setValue,
    watch,
    formState: { isValid, isDirty },
  } = form;

  let { data: dataFunctionalities, functionalitiesAction } = useFunctionalities("DATA");
  const { data: dataCategory } = useCategories("DATA");
  const { data: labelsData, reloadFn } = useConsoleTranslation("DATA");
  const [modalProps, setModalProps] = useState<NewDialogProps>({
    visible: false,
  } as NewDialogProps);
  dataFunctionalities =update ? dataFunctionalities?.filter((item: any) => item.code !== selectedFunctionality?.code): dataFunctionalities;
  let hash: any = {};
  const formKeysOptions = {
    parentId: dataFunctionalities
      ?.filter((item: any) => item.statusId === 'ACTIVE' || item.statusId === 'DRAFT')
      ?.filter((item: any) => !!item.isAdministrative == (watch('isAdministrative') === 'isAdministrative'))
      ?.map((item: any) => ({ label: item.code, value: item.code })),
    categoryId: dataCategory?.filter((item: any) => item.statusId === 'ACTIVE')?.map((item: any) => ({ label: item.name, value: item.id })),
    statusId: FUNCTIONALITIES_STATUS_OPTIONS(t)
      .sort((a, b) => a.label.localeCompare(b.label)),
    keyLabel: labelsData?.filter((item: any) => item?.functionality?.includes('Menu'))
      ?.map((item: any) => ({ label: item?.key, value: item?.key }))
      ?.filter((item: any) => {
        const exists = !hash[item.value];
        hash[item.value] = true;
        return exists;
      })
      ?.sort((a: any, b: any) => a.label.localeCompare(b.label)),
  };
  const radioButtonOptions = {
    isAdministrative: [
      {
        label: 'Functionalities.isApplicationOptionLabel',
        value: "isApplication"
      },
      {
        label: 'Functionalities.isAdministrativeOptionLabel',
        value: "isAdministrative"
      }
    ],
  };

  const optionalFieldsObject = {
    expeditionDate: true,
    expirationDate: true,
  }

  const [calendarOptions, setCalendarOptions] = useState<any>({
    expeditionDate: {
      value: undefined,
      minDate: new Date(),
      maxDate: undefined,
      disabled: false,
    },
    expirationDate: {
      value: undefined,
      minDate: new Date(),
      maxDate: undefined,
      disabled: ((update || clone) && form?.getValues('expirationDate') !== '') ? false : true,
    },
  })

  const onReturn = () => {
    setAppState((prev) => ({
      ...prev,
      selectedFunctionality: null,
    }));
    navigate('/functionalities');
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    const actionStatus = await functionalitiesAction(update ? "UPDATE" : "CREATE",
      update ?
        {
          ...formValues,
          keyLabel: 'Menu.' + getValues('keyLabel'),
          expeditionDate: getValues('expeditionDate') ? getValues('expeditionDate').toISOString().slice(0, -5).replace('T', ' ') : null,
          expirationDate: getValues('expirationDate') ? getValues('expirationDate').toISOString().slice(0, -5).replace('T', ' ') : null,
          isAdministrative: getValues('isAdministrative') === 'isAdministrative'
        } : {
          ...formValues,
          keyLabel: 'Menu.' + getValues('keyLabel'),
          expeditionDate: getValues('expeditionDate') !== null ? getValues('expeditionDate')?.toISOString().slice(0, -5).replace('T', ' ') : getValues('expeditionDate'),
          expirationDate: getValues('expirationDate') !== null ? getValues('expirationDate')?.toISOString().slice(0, -5).replace('T', ' ') : getValues('expirationDate'),
          isAdministrative: getValues('isAdministrative') === 'isAdministrative'
        }
    );
    if (actionStatus === "SUCCESS") {
      navigate(-1);
      window.scrollTo(0, 0);
    }
  };

  const onEdit = () => {
    setValue('expeditionDate', formatStringToDate2(selectedFunctionality?.expeditionDate || '', false), REVALIDATE_FORM_PROPS);
    setValue('expirationDate', formatStringToDate2(selectedFunctionality?.expirationDate, false), REVALIDATE_FORM_PROPS);
    navigate('/functionalities/editfunctionalities');
  };

  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`Functionalities.${update ? "save" : "create"}ModalTitle`),
          content: t(`Functionalities.${update ? "save" : "create"}ModalContent`),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Functionalities.cancelModalTitle"),
          content: t("Functionalities.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  const functionalitiesFields = functionalitiesFieldsSelecter(
    update
      ? 'update'
      : clone
        ? 'clone'
        : detail
          ? 'detail'
          : 'default'
  );

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-60px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            <span style={{ color: "var(--cardBackgroundColorOneShade80)" }}>
              {
                update
                  ? t("Functionalities.editFunctionalitiesTitle")
                  : clone
                    ? t("Functionalities.cloneFunctionalitiesTitle")
                    : detail
                      ? t("Functionalities.detailFunctionalitiesTitle")
                      : t("Functionalities.createFunctionalitiesTitle")
              }
            </span>
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("Functionalities.functionalitiesTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("ConsoleTranslations.adminConsoleMenuLabel"),
              disabled: true
            },
            {
              label: t("Functionalities.functionalitiesTitle"),
              command: () => onReturn(),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: update
                ? t("Functionalities.editFunctionalitiesBreadcrumb")
                : clone
                  ? t("Functionalities.cloneFunctionalitiesBreadcrumb")
                  : detail
                    ? t("Functionalities.detailFunctionalitiesBreadcrumb")
                    : t("Functionalities.createFunctionalitiesBreadcrumb"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <div id='functionalities-customformcomponent'>
        <CustomFormComponent
          functionality="Functionalities"
          fields={functionalitiesFields}
          form={form}
          schemaOfValidation={FunctionalitiesFormSchema}
          dropdownOptions={formKeysOptions}
          radioButtonOptions={radioButtonOptions}
          boxTitle={
            update
              ?
              t('Functionalities.editFunctionalitiesDescription')
              : clone
                ? t('Functionalities.cloneFunctionalitiesDescription')
                : detail
                  ? ''
                  : t('Functionalities.createFunctionalitiesDescription')
          }
          isOptional={optionalFieldsObject}
          language={localStorage.getItem('LANGUAGE') || 'en'}
          calendarTranslations={calendarTranslations}
          calendarOptions={calendarOptions}
          setCalendarOptions={setCalendarOptions}
          REVALIDATE_FORM_PROPS={REVALIDATE_FORM_PROPS}
          readOnlyObject={{
            key: (update || detail) ? true : false,
            code: (update || detail) ? true : false,
            nametoclone: clone ? true : false,
            codetoclone: clone ? true : false,
            expeditionDate: detail ? true : false,
            expirationDate: detail ? true : false,
            categoryId: detail ? true : false,
            keyLabel: detail ? true : false,
            keyLabelDescription: detail ? true : false,
            parentId: detail ? true : false,
            statusId: detail ? true : false,
            isAdministrative: detail ? true : false,
          }}
          dropdownFilterObject={{ keyLabel: true }}
          customComponent={createLabelButtonMaker({
            setModalProps,
            dialogTitle: t('ConsoleTranslations.createLabelTitle'),
            dialogDetail: t('ConsoleTranslations.createLabelDescription'),
            buttonLabel: t('ConsoleTranslations.createLabelTitle'),
            functionality: 'Menu',
            actionsToExecute: (key: string) => {
              reloadFn();
              setValue("keyLabel", key, REVALIDATE_FORM_PROPS)
            },
            isAdminFunctionality: true,
          })}
        />
      </div>

      <BoxFull className="pt-0 px-4 pb-5" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          {!detail &&
            <Button
              variant="p-button-link"
              label={t("Functionalities.cancelBtnLabel")}
              onClick={isDirty ? () => handleModalProps("cancel") : onReturn}
            />
          }
          <Button
            variant="p-button"
            label={
              update
                ? t("Functionalities.saveBtnLabel")
                : clone
                  ? t("Functionalities.cloneBtnLabel")
                  : detail
                    ? t("Functionalities.editBtnLabel")
                    : t("Functionalities.createBtnLabel")
            }
            disabled={update || clone ? !(isValid && isDirty) : !isValid}
            onClick={detail ? onEdit : () => handleModalProps("action")}
          />
        </div>
      </BoxFull>

      <NewCustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
        aditionalClass="new-functionality-component-dialog overlay-createlabelsbutton"
        acceptLabel={modalProps.acceptLabel}
        rejectLabel={modalProps.rejectLabel}
        onReject={modalProps.onReject}
      />
    </>
  );
};